import { ReactComponent as Logo } from "logic/src/components/icons/logo.svg";
import { ReactComponent as CloudIcon } from "./cloud.svg";
import { ReactComponent as AddIcon } from "logic/src/components/icons/add.svg";
import { ReactComponent as LinkIcon } from "logic/src/components/icons/link.svg";
import styles from "./index.module.scss";
import { Input } from "antd";
// import cls from "classnames";
import { DragEvent, useRef, useState } from "react";
import useMessage from "logic/src/utils/useMessage";
import {
  EVideoUploadStatus,
  IUploadCheckResult,
  IVideoFileUploadInfo,
} from "logic/src/objects/video/types";
import Button from "@/components/SJButton";
import VideoThumbnail from "./components/VideoThumbnail/index";
import { VIDEO_DISABLE_TIP } from "logic/src/constants/user";
import { MAX_VIDEO_NUM } from "logic/src/objects/video/constants";

// 视频文件大小限制 300MB
const MAX_VIDEO_SIZE = 300 * 1024 * 1024;
// 视频最大分辨率支持，宽高更大值不超过1920，更小值不超过1080，即1080P
// 支持的视频格式
const ACCEPT_VIDEO_TYPES = ".mp4,.mov,.avi";

interface VideoUploadProps {
  uploadUrl: (url: string) => Promise<number>;
  uploadVideo: (file: File[]) => Promise<IUploadCheckResult>;
  fileList: IVideoFileUploadInfo[];
  onConfirm: () => void;
  disabled: boolean;
  fileUploading: boolean;
  onDelete: (v: IVideoFileUploadInfo) => void;
}

const Recommend = () => {
  return (
    <div className={styles.recommend}>
      <div>1、支持上传MP4、AVI、MOV等格式的视频文件</div>
      <div>2、单次最多上传 {MAX_VIDEO_NUM} 个视频，且大小不超过300M左右</div>
      <div> 3、支持对10分钟内视频进行理解、思考、剪辑，时长越短效果越佳</div>
    </div>
  );
};

const UrlRecommend = () => {
  return (
    <div className={styles.recommend}>
      <div>
        <strong>上传要求</strong>
        ：1、支持上传在线视频链接，链接中的视频大小不超过300M。
      </div>
      <div>2、支持对时长10分钟以内的视频进行视频理解、推理和编辑</div>
    </div>
  );
};

const VideoUpload = ({
  uploadVideo,
  onConfirm,
  fileList,
  disabled,
  fileUploading,
  onDelete,
  uploadUrl,
}: VideoUploadProps) => {
  const [isUploading] = useState(true);
  const [url, setUrl] = useState("");
  const message = useMessage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 处理文件选择
  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const curFileList = [...files];

    if (curFileList.length + fileList.length > MAX_VIDEO_NUM) {
      message.error(`最多上传 ${MAX_VIDEO_NUM} 个视频`);
      return;
    }

    const isValidType = curFileList.every(file => {
      const fileType = file.name.toLowerCase().split(".").pop();
      const isValidType = ACCEPT_VIDEO_TYPES.includes(`.${fileType}`);
      return isValidType;
    });

    // 检查文件类型
    if (!isValidType) {
      message.error("请上传 MP4、MOV、AVI格式的视频文件");
      return;
    }

    const sizeNotOk = curFileList.some(file => file.size > MAX_VIDEO_SIZE);

    // 检查文件大小
    if (sizeNotOk) {
      message.error("视频文件大小不能超过 300MB");
      return;
    }
    try {
      // 处理文件上传
      console.log("开始上传视频:", curFileList);
      const res = await uploadVideo(curFileList);
      const { durationInvalid, durationError, resolutionInvalid } = res;
      console.log("res ==>", res);
      if (durationError) {
        message.error("暂不支持该视频");
      }
      if (durationInvalid) {
        message.error("视频总时长不能超过10分钟");
      }
      if (resolutionInvalid) {
        message.error("视频分辨率过大，不能超过1080*1920");
      }
    } catch (error) {
      console.error("视频上传失败:", error);
      message.error("视频上传失败，请重试");
    }
  };

  // 处理点击上传
  const handleClick = () => {
    if (disabled) {
      return message.info(VIDEO_DISABLE_TIP);
    }
    fileInputRef.current?.click();
  };

  // 处理拖拽
  const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (fileList.length >= MAX_VIDEO_NUM) {
      return message.error(`最多上传 ${MAX_VIDEO_NUM} 个视频`);
    }
    if (disabled) {
      return message.info(VIDEO_DISABLE_TIP);
    }
    const files = event.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleComfirm = async () => {
    if (isUploading) {
      if (fileList.length === 0) {
        message.error("请上传视频");
        return;
      }
      onConfirm();
    } else {
      if (!url) {
        message.error("请输入视频链接");
        return;
      }

      if (
        !/^(https?:\/\/)([a-zA-Z0-9-._~%!$&'()*+,;=]+@)?([a-zA-Z0-9.-]+)(:\d+)?(\/[a-zA-Z0-9-._~%!$&'()*+,;=:@/]*)?(\?[a-zA-Z0-9-._~%!$&'()*+,;=:@/?]*)?(#[a-zA-Z0-9-._~%!$&'()*+,;=:@/?]*)?$/.test(
          url,
        )
      ) {
        message.error("请输入正确的视频链接");
        return;
      }
      try {
        const res = await uploadUrl(url);
        if (res === 16000) {
          message.error("您暂时无视频编辑权限");
        } else if (res === 190004) {
          message.error("超过视频提交上限");
        }
      } catch (err) {
        message.error("视频链接无效");
      }
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <Logo className={styles.logo} />
        <div className={styles.text}>
          <h1 className={styles.title}>你好，我是商量视频理解助手</h1>
          <span className={styles.betaText}>试用</span>
        </div>
      </div>
      {/* <Segmented
        className={styles.segmented}
        options={["本地上传", "链接上传"]}
        block
      /> */}
      {/* <div className={styles.segmented}>
        <div
          onClick={() => setIsUploading(true)}
          className={cls(styles.item, isUploading && styles.selected)}
        >
          本地上传
        </div>
        <div
          onClick={() => setIsUploading(false)}
          className={cls(styles.item, !isUploading && styles.selected)}
        >
          链接上传
        </div>
      </div> */}
      {isUploading &&
        (fileList && fileList.length ? (
          <div
            className={styles.videoBox}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div className={styles.videoList}>
              {fileList.map((item, index) => {
                return item.file ? (
                  <VideoThumbnail
                    key={index}
                    file={item.file}
                    loading={item.uploadStatus === EVideoUploadStatus.UPLOADING}
                    error={
                      item.uploadStatus === EVideoUploadStatus.UPLOAD_FAILED
                    }
                    thumbnailUrl={item.thumbnailUrl}
                    onDelete={() => onDelete(item)}
                  />
                ) : null;
              })}
              {fileList.length < MAX_VIDEO_NUM && (
                <div className={styles.addBtn} onClick={handleClick}>
                  <AddIcon className={styles.addBtnIcon} />
                </div>
              )}
            </div>
            <Recommend />
          </div>
        ) : (
          <div
            className={styles.uploadArea}
            onClick={handleClick}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div className={styles.iconWrap}>
              <CloudIcon className={styles.cloudIcon} />
            </div>
            <div className={styles.uploadText}>
              <span className={styles.uploadLink}>点击上传</span>
              <span className={styles.uploadOr}>或</span>
              <span className={styles.uploadDrag}>拖拽视频到此处</span>
            </div>
            <Recommend />
          </div>
        ))}
      {!isUploading && (
        <div className={styles.inputArea}>
          <Input
            value={url}
            disabled={disabled}
            onChange={e => setUrl(e.target.value)}
            placeholder="请输入视频链接"
            className={styles.input}
            prefix={<LinkIcon className={styles.icon} />}
          ></Input>
          <UrlRecommend />
        </div>
      )}
      <div className={styles.videoSubmitBtnBox}>
        <Button
          type="primary"
          size="large"
          disabled={(isUploading && fileUploading) || (!isUploading && !url)}
          className={styles.videoSubmitBtn}
          onClick={handleComfirm}
        >
          确认
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple={MAX_VIDEO_NUM > 1}
        accept={ACCEPT_VIDEO_TYPES}
        style={{ display: "none" }}
        onChange={e => {
          handleFileSelect(e.target.files);
          e.target.value = "";
        }}
      />
      {message.contextHolder}
    </div>
  );
};

export default VideoUpload;
